<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg shadow-lg border border-gray-700 transform transition-all duration-300 animate-scaleIn p-5 relative overflow-hidden">
      <!-- Tabs -->
      <div class="flex border-b border-gray-700">
        <button
          v-for="tab in tabs"
          :key="tab"
          @click="currentTab = tab"
          :class="[
            'flex-1 py-2 px-4 text-center text-sm font-semibold transition-colors duration-200 relative',
            currentTab === tab
              ? 'bg-gray-700 text-gray-300 border-b-2 border-indigo-600'
              : 'text-gray-400 hover:text-indigo-300 hover:bg-gray-700'
          ]"
        >
          {{ tab === 'move' ? 'ย้ายห้อง' : 'ตั้งค่า' }}
          <span v-if="currentTab === tab" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-8">
        <div class="animate-spin-pulse rounded-full h-8 w-8 border-t-2 border-red-600"></div>
      </div>

      <div v-else class="p-4">
        <!-- Move Room Tab -->
        <div v-if="currentTab === 'move'" class="animate-fadeIn">
          <div
            class="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-72 overflow-y-auto pr-2 scrollbar-thin"
            ref="roomList"
          >
            <div
              v-for="room in roomArray"
              :key="room"
              @click="selectRoom(room)"
              :data-room="room"
              :class="[
                'transition-all duration-200 rounded-md p-3 text-center cursor-pointer border relative',
                selectedRoom === room
                  ? 'bg-indigo-600 text-white border-indigo-700'
                  : selectedTempRoom === room
                    ? 'bg-green-600 text-white border-green-700'
                    : 'bg-gray-700 text-gray-300 border-gray-600 hover:border-indigo-600',
                shakeRoom === room ? 'animate-shake border-red-600' : ''
              ]"
            >
              <div><i class="fa-solid fa-door-closed text-indigo-300"></i></div>
              <div class="text-xs sm:text-sm font-medium">ห้อง {{ room }}</div>
            </div>
          </div>

          <transition name="fade">
            <div v-if="selectedTempRoom && selectedTempRoom !== selectedRoom" class="mt-4 space-y-2">
              <button
                @click="submitMoveRoom"
                :disabled="isSubmitting"
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-md text-sm transition-colors duration-200 flex justify-center items-center border border-indigo-700"
              >
                <span v-if="!isSubmitting">ย้ายไปห้อง {{ selectedTempRoom }}</span>
                <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
              </button>

              <button
                @click="closeModal"
                :disabled="isSubmitting"
                class="w-full bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-md text-sm transition-colors duration-200 border border-gray-600"
              >
                ยกเลิก
              </button>
            </div>
          </transition>

          <div
            v-if="errorMessage"
            class="mt-3 text-center text-sm"
            :class="[errorMessage === 'ย้ายห้องสำเร็จ' ? 'text-indigo-300' : 'text-red-300']"
          >
            {{ errorMessage }}
          </div>
        </div>

        <!-- Settings Tab -->
        <div v-if="currentTab === 'settings'" class="space-y-4 max-h-72 overflow-auto animate-fadeIn" id="style-1">
          <!-- Auto / Manual Toggle -->
          <div class="relative bg-gray-700 rounded-full p-1 flex items-center justify-between border border-gray-600">
            <div
              :class="[
                'absolute h-full rounded-full transition-all duration-300 ease-in-out',
                moveMode === 'auto' ? 'w-1/2 left-0 bg-indigo-600' : 'w-1/2 left-1/2 bg-indigo-600'
              ]"
            ></div>

            <button
              @click="toggleMoveMode('auto')"
              :disabled="isSubmitting"
              :class="[
                'flex-1 py-2 text-sm font-semibold z-10 transition-colors duration-300',
                moveMode === 'auto' ? 'text-gray-100' : 'text-gray-400 hover:text-indigo-300'
              ]"
            >
              อัตโนมัติ
            </button>

            <button
              @click="toggleMoveMode('manual')"
              :disabled="isSubmitting"
              :class="[
                'flex-1 py-2 text-sm font-semibold z-10 transition-colors duration-300',
                moveMode === 'manual' ? 'text-gray-100' : 'text-gray-400 hover:text-indigo-300'
              ]"
            >
              กำหนดเอง
            </button>
          </div>

          <!-- Manual Room Sequence -->
          <transition name="slide-fade">
            <div v-if="moveMode === 'manual' && !settingsLoading" class="space-y-3">
              <div
                v-for="(move, index) in moveSequence"
                :key="index"
                class="flex items-center gap-2 animate-slide-in"
              >
                <span class="text-sm text-gray-300 whitespace-nowrap">ครั้งที่ {{ index + 1 }}:</span>

                <select
                  v-model="moveSequence[index]"
                  @change="validateRoomSelection(index)"
                  :disabled="isSubmitting"
                  class="flex-1 bg-gray-700 text-gray-300 border border-gray-600 rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
                >
                  <option value="" disabled>เลือกห้อง</option>
                  <option v-for="room in roomArray" :key="room" :value="room">ห้อง {{ room }}</option>
                </select>

                <button
                  v-if="moveSequence.length > 1"
                  @click="removeMoveSequence(index)"
                  :disabled="isSubmitting"
                  type="button"
                  class="bg-yellow-600 hover:bg-yellow-700 text-white rounded-md p-1.5 text-sm transition-colors duration-200 border border-yellow-700"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>

              <button
                v-if="moveSequence.length === 0 || moveSequence[moveSequence.length - 1]"
                @click="addMoveSequence"
                :disabled="isSubmitting"
                type="button"
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-md text-sm transition-colors duration-200 flex justify-center items-center border border-indigo-700"
              >
                <i class="fas fa-plus mr-2"></i> เพิ่มครั้ง
              </button>

              <div v-if="settingsError" class="text-red-300 text-sm text-center">
                {{ settingsError }}
              </div>

              <div
                v-if="successMessage"
                class="text-indigo-300 text-sm text-center"
              >
                {{ successMessage }}
              </div>
            </div>

            <div v-else-if="moveMode === 'manual'" class="flex items-center justify-center py-8">
              <div class="animate-spin-pulse rounded-full h-6 w-6 border-t-2 border-red-600"></div>
            </div>
          </transition>

          <!-- Save Settings -->
          <button
            v-if="!settingsLoading"
            @click="saveSettings"
            :disabled="isSubmitting"
            class="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-md text-sm transition-colors duration-200 flex justify-center items-center border border-indigo-700"
          >
            <span v-if="!isSubmitting"><i class="fas fa-save mr-1"></i> บันทึกการตั้งค่า</span>
            <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
        </div>
      </div>

      <!-- Close -->
      <button
        @click="closeModal"
        :disabled="isSubmitting"
        class="absolute top-3 right-3 text-gray-400 hover:text-indigo-300 transition-colors duration-200 group"
      >
        <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import getUser from '@/composables/getUser'
import { usePlayerStore } from '@/composables/usePlayerStore'
import { projectFunctions } from '@/firebase/config'

export default {
  name: 'MoveRoomModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close'],

  setup (props, { emit }) {
    const { user } = getUser()
    const playerStore = usePlayerStore()

    const totalRooms = ref(0)
    const selectedRoom = ref(0)
    const selectedTempRoom = ref(null)
    const isSubmitting = ref(false)
    const errorMessage = ref('')
    const settingsError = ref('')
    const successMessage = ref('')
    const loading = ref(true)
    const settingsLoading = ref(false)
    const roomList = ref(null)
    const shakeRoom = ref(null)
    const currentTab = ref('move')
    const tabs = ['move', 'settings']
    const moveMode = ref('auto')
    const moveSequence = ref([])

    const roomArray = computed(() => {
      const rooms = []

      for (let i = 1; i <= totalRooms.value; i++) {
        rooms.push(i)
      }

      return rooms
    })

    const scrollToCurrentRoom = () => {
      if (!roomList.value || !selectedRoom.value) return

      nextTick(() => {
        const currentElement = roomList.value.querySelector(`[data-room="${selectedRoom.value}"]`)

        if (currentElement) {
          currentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    }

    const fetchRoomData = async () => {
      if (!user.value) {
        errorMessage.value = 'กรุณาล็อกอินก่อน'
        loading.value = false
        return
      }

      try {
        errorMessage.value = ''
        settingsError.value = ''
        successMessage.value = ''

        const charData = playerStore.characterData

        if (charData) {
          totalRooms.value = charData.room || 0
        } else {
          errorMessage.value = 'ไม่พบข้อมูลตัวละคร'
        }

        const charPrivateData = playerStore.characterPrivateData

        if (charPrivateData) {
          selectedRoom.value = charPrivateData.selectedRoom || 0
          moveMode.value = charPrivateData.moveMode || 'auto'

          const savedSequence = Array.isArray(charPrivateData.moveSequence)
            ? charPrivateData.moveSequence
            : []

          moveSequence.value = savedSequence.map(room => Number(room)).filter(room => room > 0)

          if (moveSequence.value.length === 0 && moveMode.value === 'manual') {
            moveSequence.value = ['']
          }
        } else {
          errorMessage.value = 'ไม่พบข้อมูลส่วนตัวของตัวละคร'
        }

        scrollToCurrentRoom()
      } catch (err) {
        console.error('Error fetching room data:', err)
        errorMessage.value = 'ไม่สามารถโหลดข้อมูลได้'
      } finally {
        loading.value = false
      }
    }

    const saveSettings = async () => {
      if (isSubmitting.value) return

      settingsError.value = ''
      successMessage.value = ''
      errorMessage.value = ''

      let payloadMoveSequence = []

      if (moveMode.value === 'manual') {
        const cleanedSequence = moveSequence.value
          .map(room => Number(room))
          .filter(room => room > 0)

        if (cleanedSequence.length === 0) {
          settingsError.value = 'กรุณาเลือกห้องอย่างน้อย 1 ครั้ง'
          return
        }

        if (cleanedSequence.some(room => room < 1 || room > totalRooms.value)) {
          settingsError.value = `กรุณาเลือกห้องระหว่าง 1 ถึง ${totalRooms.value}`
          return
        }

        moveSequence.value = cleanedSequence
        payloadMoveSequence = cleanedSequence
      }

      isSubmitting.value = true

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions')

        const result = await gameActions({
          action: 'roomHandler',
          subAction: 'updateMoveSettings',
          moveMode: moveMode.value,
          moveSequence: moveMode.value === 'manual' ? payloadMoveSequence : []
        })

        successMessage.value = result.data?.message || 'บันทึกการตั้งค่าสำเร็จ'

        /*
          สำคัญ:
          ไม่ closeModal() ตรงนี้แล้ว
          เพื่อให้ผู้ใช้บันทึกแล้วกด "เพิ่มครั้ง" ต่อได้ทันที
        */
      } catch (err) {
        console.error('Error saving settings:', err)
        settingsError.value = err.message || 'เกิดข้อผิดพลาดในการบันทึกการตั้งค่า'
      } finally {
        isSubmitting.value = false
      }
    }

    const toggleMoveMode = async (mode) => {
      if (isSubmitting.value) return
      if (moveMode.value === mode) return

      settingsError.value = ''
      successMessage.value = ''

      if (mode === 'manual') {
        settingsLoading.value = true

        await new Promise(resolve => setTimeout(resolve, 300))

        moveSequence.value = moveSequence.value.length ? moveSequence.value : ['']
        settingsLoading.value = false
      }

      moveMode.value = mode
    }

    const selectRoom = (room) => {
      if (isSubmitting.value || room === selectedRoom.value) return

      selectedTempRoom.value = room
      errorMessage.value = ''
    }

    const validateRoomSelection = (index) => {
      const selected = Number(moveSequence.value[index])

      successMessage.value = ''

      if (selected && (selected < 1 || selected > totalRooms.value)) {
        settingsError.value = `กรุณาเลือกห้องระหว่าง 1 ถึง ${totalRooms.value}`
        moveSequence.value[index] = ''
        return
      }

      settingsError.value = ''
    }

    const addMoveSequence = () => {
      if (isSubmitting.value) return

      const lastRoom = moveSequence.value[moveSequence.value.length - 1]

      if (moveSequence.value.length > 0 && !lastRoom) {
        settingsError.value = 'กรุณาเลือกห้องของครั้งล่าสุดก่อนเพิ่มครั้งใหม่'
        return
      }

      moveSequence.value.push('')
      settingsError.value = ''
      successMessage.value = ''
    }

    const removeMoveSequence = (index) => {
      if (isSubmitting.value) return

      moveSequence.value.splice(index, 1)

      if (moveSequence.value.length === 0 && moveMode.value === 'manual') {
        moveSequence.value.push('')
      }

      settingsError.value = ''
      successMessage.value = ''
    }

    const closeModal = () => {
      if (isSubmitting.value) return

      selectedTempRoom.value = null
      errorMessage.value = ''
      settingsError.value = ''
      successMessage.value = ''
      emit('close')
    }

    const submitMoveRoom = async () => {
      if (!selectedTempRoom.value || selectedTempRoom.value === selectedRoom.value) {
        errorMessage.value = 'กรุณาเลือกห้องใหม่'
        shakeRoom.value = selectedTempRoom.value

        setTimeout(() => {
          shakeRoom.value = null
        }, 500)

        return
      }

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions')

        const result = await gameActions({
          action: 'roomHandler',
          subAction: 'moveRoom',
          roomNumber: selectedTempRoom.value
        })

        selectedRoom.value = selectedTempRoom.value
        selectedTempRoom.value = null
        scrollToCurrentRoom()
        errorMessage.value = result.data?.message || 'ย้ายห้องสำเร็จ'
      } catch (err) {
        console.error('Error moving room:', err)
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการย้ายห้อง'
      } finally {
        isSubmitting.value = false
      }
    }

    watch(
      () => props.isOpen,
      (newVal) => {
        if (newVal) {
          loading.value = true
          currentTab.value = 'move'
          fetchRoomData()
        }
      }
    )

    onMounted(() => {
      if (props.isOpen) {
        fetchRoomData()
      }
    })

    return {
      totalRooms,
      selectedRoom,
      selectedTempRoom,
      roomArray,
      isSubmitting,
      errorMessage,
      settingsError,
      successMessage,
      loading,
      settingsLoading,
      roomList,
      shakeRoom,
      currentTab,
      tabs,
      moveMode,
      moveSequence,
      selectRoom,
      closeModal,
      submitMoveRoom,
      toggleMoveMode,
      validateRoomSelection,
      addMoveSequence,
      removeMoveSequence,
      saveSettings
    }
  }
}
</script>

<style scoped>
/* Animations */
@keyframes modalFade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0.95);
  }

  to {
    transform: scale(1);
  }
}

.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

@keyframes tabSlide {
  from {
    width: 0;
  }

  to {
    width: 100%;
  }
}

.animate-tabSlide {
  animation: tabSlide 0.2s ease-out;
}

@keyframes slide-in {
  from {
    transform: translateX(-20px);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  50% {
    transform: translateX(5px);
  }

  75% {
    transform: translateX(-5px);
  }

  100% {
    transform: translateX(0);
  }
}

.animate-shake {
  animation: shake 0.5s ease-in-out;
}

@keyframes spin-pulse {
  0% {
    transform: rotate(0deg) scale(1);
  }

  50% {
    transform: rotate(180deg) scale(1.2);
  }

  100% {
    transform: rotate(360deg) scale(1);
  }
}

.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}

/* Scrollbar styles */
.scrollbar-thin::-webkit-scrollbar,
#style-1::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-thin::-webkit-scrollbar-track,
#style-1::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb,
#style-1::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover,
#style-1::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.scrollbar-thin,
#style-1 {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Transition */
.fade-enter-active,
.fade-leave-active,
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.25s ease;
}

.fade-enter-from,
.fade-leave-to,
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* Button styles */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
</style>